import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import RialSymbol from '../components/RialSymbol';
import customerService from '../services/customer.service';
import {
  cartDisplayName,
  cartItemCount,
  cartSubtotal,
  useMenuCart,
} from '../services/menuCart';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  CreditCard as CreditCardIcon,
  InfoOutlined as InfoIcon,
  Payments as CashIcon,
} from '@mui/icons-material';

interface PlacedOrder {
  businessName: string;
  itemCount: number;
  total: number;
  pickupCode: string;
  orderId: string;
}

/**
 * Checkout for standalone menu-items orders (no surprise box) — mirrors
 * Checkout.tsx's flow, but works off the menu cart instead of the
 * surprise-box cart.
 *
 * A cart can span several restaurants. Each restaurant is placed as its own
 * order, because a pickup happens at one restaurant and each merchant runs
 * the status of their own order.
 */
export default function MenuCheckout() {
  const navigate = useNavigate();
  const cart = useMenuCart();
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [placedOrders, setPlacedOrders] = useState<PlacedOrder[] | null>(null);
  const [failures, setFailures] = useState<string[]>([]);

  if (placedOrders) {
    const multiple = placedOrders.length > 1;
    const grandTotal = placedOrders.reduce((sum, o) => sum + o.total, 0);
    return (
      <Box sx={{ maxWidth: 520, mx: 'auto', py: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {multiple ? 'Orders placed' : 'Order placed'}
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            {multiple
              ? `${placedOrders.length} restaurants are preparing your items. Each has its own pickup code.`
              : `Your order from ${placedOrders[0].businessName} is being prepared.`}
          </Typography>
        </Box>

        {failures.length > 0 && (
          <Alert severity="error" sx={{ mb: 2.5 }}>
            <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
              Some orders could not be placed
            </Typography>
            {failures.map((failure) => (
              <Typography key={failure} variant="body2">
                {failure}
              </Typography>
            ))}
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Those items are still in your cart.
            </Typography>
          </Alert>
        )}

        {placedOrders.map((order) => (
          <Card key={order.orderId || order.pickupCode} sx={{ p: 3, mb: 2, textAlign: 'center' }}>
            {multiple && (
              <Typography sx={{ fontWeight: 700, mb: 1 }}>{order.businessName}</Typography>
            )}
            <Typography variant="overline" color="primary.main" sx={{ fontWeight: 700 }}>
              Pickup Code
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: 4, my: 1 }}>
              {order.pickupCode}
            </Typography>
            <Divider sx={{ my: 1.5 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                {order.itemCount} item{order.itemCount === 1 ? '' : 's'}
              </Typography>
              <Typography sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <RialSymbol /> {order.total.toFixed(2)}
              </Typography>
            </Box>
          </Card>
        ))}

        {multiple && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1, mb: 3 }}>
            <Typography sx={{ fontWeight: 700 }}>Total paid</Typography>
            <Typography sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <RialSymbol /> {grandTotal.toFixed(2)}
            </Typography>
          </Box>
        )}

        <Button
          variant="contained"
          fullWidth
          onClick={() =>
            multiple || !placedOrders[0].orderId
              ? navigate('/orders')
              : navigate(`/orders/${placedOrders[0].orderId}`)
          }
        >
          {multiple ? 'View My Orders' : 'View Order Details'}
        </Button>
      </Box>
    );
  }

  if (cart.carts.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 10 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 18, mb: 1 }}>Your cart is empty</Typography>
        <Button variant="contained" onClick={() => navigate('/restaurants')}>
          Browse Restaurants
        </Button>
      </Box>
    );
  }

  const multipleRestaurants = cart.carts.length > 1;

  const handlePlaceOrder = async () => {
    setLoading(true);
    setError('');

    const placed: PlacedOrder[] = [];
    const failed: string[] = [];
    const succeededBusinessIds: string[] = [];

    // One order per restaurant, so each gets its own pickup code and status.
    for (const restaurantCart of cart.carts) {
      try {
        const order = await customerService.createMenuOrder(
          restaurantCart.lines.map((l) => ({ menuItem: l.item._id, quantity: l.quantity })),
          paymentMethod
        );
        placed.push({
          businessName: cartDisplayName(restaurantCart),
          itemCount: cartItemCount(restaurantCart),
          total: cartSubtotal(restaurantCart),
          pickupCode: order?.pickupCode || '',
          orderId: order?._id || '',
        });
        succeededBusinessIds.push(restaurantCart.businessId);
      } catch (err: any) {
        const message =
          err.response?.data?.error?.message ||
          err.message ||
          'something went wrong placing this order.';
        failed.push(`${cartDisplayName(restaurantCart)}: ${message}`);
      }
    }

    // Only the restaurants that actually went through leave the cart — the
    // rest stay put so the customer can retry just those.
    succeededBusinessIds.forEach((businessId) => cart.clearRestaurant(businessId));

    setLoading(false);

    if (placed.length === 0) {
      setError(failed.join(' ') || 'Could not place your order. Please try again.');
      return;
    }

    setFailures(failed);
    setPlacedOrders(placed);
  };

  return (
    <Box sx={{ maxWidth: 560, mx: 'auto' }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
        Checkout
      </Typography>

      {cart.carts.map((restaurantCart) => (
        <Card key={restaurantCart.businessId} sx={{ p: 2.5, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Typography sx={{ fontWeight: 700, flex: 1, minWidth: 0 }} noWrap>
              {cartDisplayName(restaurantCart)}
            </Typography>
            {multipleRestaurants && (
              <Chip size="small" color="primary" variant="outlined" label="Separate order" />
            )}
          </Box>
          {restaurantCart.lines.map(({ item, quantity }) => (
            <Box key={item._id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
              <Typography variant="body2" color="text.secondary">
                {quantity}x {item.name}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <RialSymbol /> {(item.price * quantity).toFixed(2)}
              </Typography>
            </Box>
          ))}
          <Divider sx={{ my: 1.5 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography sx={{ fontWeight: 700 }}>Subtotal</Typography>
            <Typography sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <RialSymbol /> {cartSubtotal(restaurantCart).toFixed(2)}
            </Typography>
          </Box>
        </Card>
      ))}

      {multipleRestaurants && (
        <Card sx={{ p: 2.5, mb: 2.5, bgcolor: 'action.hover' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
            <Typography sx={{ fontWeight: 700 }}>Total</Typography>
            <Typography sx={{ fontWeight: 800, fontSize: 18, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <RialSymbol /> {cart.total.toFixed(2)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <InfoIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              Placed as {cart.carts.length} separate orders — one pickup code per restaurant.
            </Typography>
          </Box>
        </Card>
      )}

      <Card sx={{ p: 2.5, mb: 2.5 }}>
        <Typography sx={{ fontWeight: 700, mb: 1.5 }}>Payment Method</Typography>
        <RadioGroup value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
          <FormControlLabel
            value="card"
            control={<Radio />}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CreditCardIcon fontSize="small" /> Card
              </Box>
            }
          />
          <FormControlLabel
            value="cash"
            control={<Radio />}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CashIcon fontSize="small" /> Cash (pay at pickup)
              </Box>
            }
          />
        </RadioGroup>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2.5 }}>
          {error}
        </Alert>
      )}

      <Button fullWidth variant="contained" size="large" disabled={loading} onClick={handlePlaceOrder}>
        {loading
          ? 'Placing order…'
          : multipleRestaurants
            ? `Place ${cart.carts.length} Orders — ${cart.total.toFixed(2)}`
            : `Place Order — ${cart.total.toFixed(2)}`}
      </Button>
    </Box>
  );
}
